import React, { useMemo, useState } from "react";
import type { BookingItem, BookingStatus } from "./preneed-booking.types";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  deletePreneedBookingDocument,
  uploadPreneedBookingDocuments,
  updateBookingStatus
} from "../../../api/preneed-booking.api";
import { useSetLoading } from "../../../common/hooks/appearance";
import { AlertComponent } from "../../../common/components";
import ActionConfirmationPopup from "../../../common/components/action-confirmation-popup/action-confirmation-popup";
import FilePreviewModal from "../../provider-section/booking-and-appointment/file-preview-modal";
import PaymentStatus from "../payments-and-billing/payment-status";
import PreneedBookingStatus from "./preneed-booking-status";

type Props = {
  booking: BookingItem | null;
  apiDetails?: any; // response from details API
  loading?: boolean;
  onBack: () => void;
  onStatusChange?: (id: string, status: BookingStatus) => void;
  orderId?: number | string;
  fetchDetails?: any;
};

export default function PreneedBookingDetails({
  booking,
  apiDetails,
  loading,
  onBack,
  orderId,
  fetchDetails,
}: Props) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const d = apiDetails?.bookingId ? apiDetails : null;

  // fallback to booking card data if API not loaded
  const bookingId = d?.bookingId ?? booking?.bookingId ?? booking?.id ?? "-";
  const status = (d?.bookingStatus ?? booking?.status ?? "-") as any;
  const statusDisplay =
    typeof status === "string" && status.length > 0
      ? status.toLowerCase() === "paid"
        ? "Confirmed"
        : status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
      : status;

  const fullName = d?.customer?.fullName ?? booking?.customerName ?? "-";
  const email = d?.customer?.email ?? booking?.email ?? "-";
  const phone = d?.customer?.phone ?? booking?.phone ?? "-";

  const address = useMemo(() => {
    const a = d?.customer?.address;
    if (!a) return booking?.address ?? "-";
    const parts = [
      a.street1,
      a.street2,
      a.city,
      a.state,
      a.country,
      a.pincode,
    ].filter(Boolean);
    return parts.join(", ");
  }, [d, booking]);

  const rawDate = d?.appointment?.appointmentDate ?? booking?.appointmentDate ?? "-";
  const rawTime = d?.appointment?.appointmentTime ?? booking?.appointmentTime ?? "-";
  const combined = rawDate !== "-" ? rawDate : rawTime;
  const hasAt = combined !== "-" && combined.includes(" at ");
  const appointmentDate = hasAt ? combined.split(" at ")[0].trim() : rawDate;
  const appointmentTime = hasAt ? combined.split(" at ")[1].trim() : (rawTime !== "-" ? rawTime : "-");

  const dateRequested =
    d?.appointment?.dateRequested ?? booking?.dateOfRequest ?? "-";

  const packageName =
    d?.packageDetails?.packageName ?? booking?.packageType ?? "-";
  const packagePrice = d?.packageDetails?.price ?? booking?.packagePrice ?? "-";
  // TODO: Replace with API value when backend is ready
  const includedServices = d?.packageDetails?.includedServices;
  //const serviceDescription = d?.serviceInformation?.serviceDescription ?? booking?.serviceDescription ?? "-";

  // const subServices = d?.subServices ?? booking?.subServices ?? [];
  // const merchandise = d?.merchandiseItems ?? booking?.merchandise ?? [];

  const paymentMethodRaw =
    d?.paymentInformation?.paymentMethod ?? booking?.paymentMethod ?? "-";
  const paymentMethod =
    typeof paymentMethodRaw === "string"
      ? paymentMethodRaw.startsWith("credit_card")
        ? "Credit Card" + paymentMethodRaw.slice("credit_card".length)
        : paymentMethodRaw.startsWith("debit_card")
          ? "Debit Card" + paymentMethodRaw.slice("debit_card".length)
          : paymentMethodRaw
      : paymentMethodRaw;
  const paymentStatusRaw =
    d?.paymentInformation?.paymentStatus ??
    (d?.paymentInformation?.payment as any)?.status ??
    ((booking as any)?.payment as any)?.status ??
    "-";
  const paymentStatus =
    paymentStatusRaw !== "-"
      ? paymentStatusRaw.charAt(0).toUpperCase() + paymentStatusRaw.slice(1)
      : "Not Initiated";
  const shouldShowAwaitingPaymentMessage =
    String(status || "")
      .trim()
      .toLowerCase() === "pending" ||
    String(statusDisplay || "")
      .trim()
      .toLowerCase() === "pending" ||
    String(paymentStatusRaw || "")
      .trim()
      .toLowerCase() === "pending";
  const isPendingPayment =
    String(paymentStatusRaw || "")
      .trim()
      .toLowerCase() === "pending";
  const displayedPaymentMethod = isPendingPayment ? "-" : paymentMethod;
  const displayedPaymentStatus = isPendingPayment
    ? "Pending"
    : paymentStatus;

  const totalAmount =
    d?.paymentInformation?.totalAmountPaid ?? booking?.totalAmount ?? "-";

  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [confirmPopup, setConfirmPopup] = useState<{
    open: boolean;
    title: string;
    note: string;
    onOkay: () => void;
  }>({ open: false, title: "", note: "", onOkay: () => { } });
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [markCompletePopupOpen, setMarkCompletePopupOpen] = useState(false);

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, doc: any) => {
    setAnchorEl(e.currentTarget);
    setSelectedDoc(doc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  const handlePreview = () => {
    setPreviewDoc(selectedDoc);
    handleMenuClose();
  };

  const handlePreviewClose = () => setPreviewDoc(null);

  const handleDownloadFile = () => {
    if (!selectedDoc?.downloadUrl) return;
    const link = document.createElement("a");
    link.href = selectedDoc.downloadUrl;
    link.target = "_blank";
    link.download = selectedDoc.originalFileName || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const filesArray = Array.from(e.target.files);

    // Avoid duplicates by name
    const filteredFiles = filesArray.filter(
      (file) => !selectedFiles.some((f) => f.name === file.name),
    );

    setSelectedFiles((prev) => [...prev, ...filteredFiles]);

    e.target.value = "";
  };

  const handleRemoveFile = (fileName: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleCancelFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveDocuments = async () => {
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("documents", file);
    });

    setIsLoading(true);

    try {
      await uploadPreneedBookingDocuments(Number(orderId), formData);
      setSelectedFiles([]);
      await fetchDetails?.(orderId);
      setAlertMessage("Documents uploaded successfully.");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message + ', ' + error?.response?.data?.details ||
        error?.message + ', ' + error?.details ||
        "Error uploading documents.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number | string) => {
    setIsLoading(true);
    try {
      await deletePreneedBookingDocument(documentId);
      await fetchDetails?.(orderId);
      setAlertMessage("Document deleted successfully.");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Error deleting document.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);
  const handleMarkAsCompleted = async () => {
    try {
      setIsLoading(true);

      await updateBookingStatus(Number(orderId));

      await fetchDetails?.(orderId);

      setAlertMessage("Order marked as completed.");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update order status.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="mg-details">
      <div className="mg-details__header">
        <button className="mg-link" onClick={onBack} type="button">
          ← Back to List
        </button>

        <div className="mg-details__titleRow">
          <div>
            <div className="mg-details__title">Order Details</div>
            <div className="mg-details__sub">
              Order ID: <b>{bookingId}</b>{" "}
              <PreneedBookingStatus status={statusDisplay} />
              {shouldShowAwaitingPaymentMessage ? (
                <span
                  className="mg-field__value"
                  style={{ marginLeft: "8px", display: "inline-block" }}
                >
                  (Awaiting payment from customer)
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mg-emptyState">Loading order details...</div>
      ) : (
        <>
          <div className="mg-card">
            <div className="mg-card__title">Customer Information</div>
            <div className="mg-2col">
              <div className="mg-field">
                <div className="mg-field__label">Full Name</div>
                <div className="mg-field__value">{fullName}</div>
              </div>

              <div className="mg-field">
                <div className="mg-field__label">Email Address</div>
                <div className="mg-field__value">{email}</div>
              </div>

              <div className="mg-field">
                <div className="mg-field__label">Phone Number</div>
                <div className="mg-field__value">{phone}</div>
              </div>

              <div className="mg-field">
                <div className="mg-field__label">Address</div>
                <div className="mg-field__value">{address}</div>
              </div>
            </div>
          </div>

          <div className={`mg-row2 mg-row2--split`}>
            <div className="mg-card">
              <div className="mg-card__title">Appointment Details</div>
              <div className="mg-3col">
                <div className="mg-field">
                  <div className="mg-field__label">Appointment Date</div>
                  <div className="mg-field__value">{appointmentDate}</div>
                </div>
                <div className="mg-field">
                  <div className="mg-field__label">Appointment Time</div>
                  <div className="mg-field__value">{appointmentTime}</div>
                </div>
                <div className="mg-field">
                  <div className="mg-field__label">Ordered Date</div>
                  <div className="mg-field__value">{dateRequested}</div>
                </div>
              </div>
            </div>

            <div className="mg-card">
              <div className="mg-card__title">Service Status</div>

              {statusDisplay?.toLowerCase() === "completed" ? (
                <div className="mg-bookingCompletedBanner">
                  <span className="mg-bookingCompletedDot" />
                  <span className="mg-bookingCompletedText">
                    Service Completed
                  </span>
                </div>
              ) : (
                <Tooltip title={statusDisplay !== "Confirmed" ? "Only Confirmed Orders can be mark as completed" : ""}>
                  <span style={{ display: "block", width: "100%" }}>
                    <button
                      type="button"
                      className="mg-markCompleteBtn"
                      onClick={() => setMarkCompletePopupOpen(true)}
                      style={{ marginTop: 8 }}
                      disabled={statusDisplay !== "Confirmed"}
                    >
                      Mark as Completed
                    </button>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="mg-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  selectedFiles.length > 0 || (d?.documents?.length ?? 0) > 0
                    ? "space-between"
                    : "flex-start",
              }}
            >
              <div className="mg-card__title">Upload Documents</div>

              {(selectedFiles.length > 0 ||
                (d?.documents?.length ?? 0) > 0) && (
                  <div
                    style={{
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      padding: "8px",
                      borderRadius: "8px",
                      fontWeight: 450,
                      marginLeft: "auto",
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span style={{ fontSize: "14px" }}>+</span> Upload Documents
                  </div>
                )}
            </div>

            {selectedFiles.length === 0 &&
              (d?.documents?.length ?? 0) === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    border: "1px solid #ccc",
                    padding: "8px",
                    cursor: "pointer",
                    borderRadius: "8px",
                    fontWeight: 450,
                    margin: "8px 0",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span style={{ fontSize: "14px" }}>+</span> Upload Documents
                </div>
              )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />

            {/* Existing DB Documents */}
            {Array.isArray(d?.documents) && d.documents.length > 0 && (
              <Box
                display="flex"
                gap={1}
                flexWrap="wrap"
                sx={{ margin: "8px 0" }}
              >
                {d.documents.map((doc: any, idx: number) => (
                  <Box
                    key={"db-" + idx}
                    sx={{
                      background: "#f5f5f5",
                      borderRadius: "16px",
                      padding: "4px 10px",
                    }}
                  >
                    {doc.originalFileName}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, doc);
                      }}
                      size="small"
                      title=""
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      open={Boolean(anchorEl) && selectedDoc?.id === doc.id}
                      anchorEl={anchorEl}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handlePreview}>Preview</MenuItem>
                      <MenuItem onClick={handleDownloadFile}>Download</MenuItem>
                      <MenuItem
                        onClick={() => {
                          setConfirmPopup({
                            open: true,
                            title: "Delete Document",
                            note: `Are you sure you want to delete ${selectedDoc?.originalFileName} document?`,
                            onOkay: () => {
                              handleDeleteDocument(selectedDoc.id);
                              setConfirmPopup((prev) => ({
                                ...prev,
                                open: false,
                              }));
                            },
                          });
                          handleMenuClose();
                        }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                    <FilePreviewModal
                      open={Boolean(previewDoc)}
                      onClose={handlePreviewClose}
                      fileUrl={previewDoc?.fileUrl || ""}
                      fileName={previewDoc?.originalFileName || ""}
                      id={previewDoc?.id || 0}
                      previewLink={previewDoc?.previewUrl || ""}
                      size={previewDoc?.size}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* New Selected Files */}
            {selectedFiles.length > 0 && (
              <Box
                display="flex"
                gap={1}
                flexWrap="wrap"
                sx={{ margin: "8px 0" }}
              >
                {selectedFiles.map((file, index) => (
                  <Box
                    key={"new-" + index}
                    display="flex"
                    alignItems="center"
                    sx={{
                      background: "#f5f5f5",
                      borderRadius: "16px",
                      padding: "4px 10px",
                    }}
                  >
                    <span style={{ marginRight: 6 }}>{file.name}</span>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(file.name)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {selectedFiles.length > 0 && (
              <div className="button-group">
                <button
                  type="button"
                  className="mg-btn mg-btn--secondary"
                  onClick={handleCancelFiles}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="mg-btn mg-btn--primary"
                  onClick={handleSaveDocuments}
                >
                  Save
                </button>
              </div>
            )}
          </div>

          <div className="mg-card">
            <div className="mg-card__title">Package Information</div>
            <div className="mg-3col">
              <div className="mg-field">
                <div className="mg-field__label">Package Name</div>
                <div className="mg-field__value">{packageName}</div>
              </div>
              <div className="mg-field">
                <div className="mg-field__label">Package Price</div>
                <div className="mg-field__value">
                  {typeof packagePrice === "number"
                    ? `$${packagePrice.toLocaleString()}`
                    : packagePrice}
                </div>
              </div>
            </div>
            {/* Included Services */}
            {includedServices && includedServices.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <div
                  className="mg-field__label"
                  style={{ marginBottom: "8px" }}
                >
                  Included Services
                </div>

                <ul style={{ margin: 0, paddingLeft: "18px" }}>
                  {includedServices.map((service: any, index: number) => (
                    <li key={index} style={{ marginBottom: "6px" }}>
                      {service.serviceName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* <div className="mg-field">
              <div className="mg-field__label">Service Description</div>
              <div className="mg-field__value">{serviceDescription}</div>
            </div> */}
          </div>

          {/* <div className="mg-card">
            <div className="mg-card__title">Sub-Services</div>
            <div className="mg-table">
              <div className="mg-table__head">
                <div>Service Name</div>
                <div className="mg-right">Price</div>
              </div>
              {(subServices ?? []).map((s: any) => (
                <div className="mg-table__row" key={s.name}>
                  <div>{s.name}</div>
                  <div className="mg-right">${Number(s.price || 0)}</div>
                </div>
              ))}
              {(!subServices || subServices.length === 0) && (
                <div className="mg-empty">No sub-services</div>
              )}
            </div>
          </div>

          <div className="mg-card">
            <div className="mg-card__title">Merchandise Items</div>
            <div className="mg-table mg-table--4">
              <div className="mg-table__head mg-table__head--4">
                <div>Item Name</div>
                <div className="mg-right">Quantity</div>
                <div className="mg-right">Unit Price</div>
                <div className="mg-right">Total</div>
              </div>

              {(merchandise ?? []).map((m: any) => {
                const qty = Number(m.quantity || 0);
                const unit = Number(m.unitPrice || 0);
                const total = qty * unit;
                return (
                  <div className="mg-table__row mg-table__row--4" key={m.name}>
                    <div>{m.name}</div>
                    <div className="mg-right">{qty}</div>
                    <div className="mg-right">${unit.toFixed(2)}</div>
                    <div className="mg-right">${total.toFixed(2)}</div>
                  </div>
                );
              })}

              {(!merchandise || merchandise.length === 0) && (
                <div className="mg-empty">No merchandise</div>
              )}
            </div>
          </div> */}

          <div className="mg-card">
            <div className="mg-card__title">Payment Information</div>
            <div className="mg-3col">
              <div className="mg-field">
                <div className="mg-field__label">Payment Method</div>
                <div className="mg-field__value">{displayedPaymentMethod}</div>
              </div>

              <div className="mg-field">
                <div className="mg-field__label">Payment Status</div>
                <div
                  className="mg-field__value"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <PaymentStatus status={displayedPaymentStatus} />
                </div>
              </div>

              <div className="mg-field">
                <div className="mg-field__label">Total Amount</div>
                <div className="mg-field__value">
                  {typeof totalAmount === "number"
                    ? `$${totalAmount.toLocaleString()}`
                    : totalAmount}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
      <ActionConfirmationPopup
        open={confirmPopup.open}
        title={confirmPopup.title}
        note={confirmPopup.note}
        onClose={() => setConfirmPopup((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmPopup.onOkay}
        confirmButtonText="Delete"
        variant="danger"
        type="delete"
      />
      {markCompletePopupOpen && (
        <div className="mg-confirmOverlay">
          <div className="mg-confirmModal">
            <h3 className="mg-confirmTitle">Confirm Completion</h3>

            <p className="mg-confirmNote">
              Are you sure you want to mark this order as complete? This
              action cannot be reversed.
            </p>

            <div className="mg-confirmActions">
              <button
                className="cancel-btn"
                onClick={() => setMarkCompletePopupOpen(false)}
              >
                Cancel
              </button>

              <button
                className="confirm-btn"
                onClick={() => {
                  setMarkCompletePopupOpen(false);
                  handleMarkAsCompleted();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
